import type { AnchorHTMLAttributes } from 'react'
import { Link } from 'react-router'
import type { ButtonSize, ButtonVariant } from './Button.tsx'
import styles from './Button.module.css'

export interface ButtonLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** An in-app route — rendered with the router's Link. */
  to?: string
  /** Anywhere else (another site, a #anchor on this page) — a plain anchor. */
  href?: string
  variant?: ButtonVariant
  size?: ButtonSize
}

/**
 * A Button that goes somewhere. Same variants, sizes and whole-button hover as
 * Button, but it's a link — so it opens in a new tab, shows its URL, and reads
 * as a link to a screen reader. Use it whenever a "button" navigates.
 */
export function ButtonLink({ to, href, variant = 'primary', size = 'md', className, children, ...props }: ButtonLinkProps) {
  const classes = [styles.button, styles.link, styles[variant], styles[size], className].filter(Boolean).join(' ')
  const content = <span className={styles.content}>{children}</span>
  if (to != null) return <Link to={to} className={classes} {...props}>{content}</Link>
  return <a href={href} className={classes} {...props}>{content}</a>
}
